/**
 * SmartSuspenseFallback - pick the right skeleton by route.
 *
 * On authenticated dashboard-shaped routes we render the full
 * AppShellSkeleton (sidebar + header + card shimmer) so the user never
 * sees a white flash after login. On public / auth / landing routes we
 * render the neutral background so the page can boot without any web-y
 * chrome peeking through.
 */
import React from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import AppShellSkeleton from './AppShellSkeleton';

const PUBLIC_EXACT = new Set([
  '/', '/auth', '/login', '/signup', '/pricing',
  '/help', '/privacy-policy', '/terms-of-service', '/cookie-policy',
  '/camera-test', '/accept-invitation', '/verify-email',
  '/verify-email-pending', '/reset-password',
  '/portal/employee', '/oauth/plaid', '/app-welcome',
]);
const PUBLIC_PREFIXES = ['/help/', '/onboarding'];

const isPublic = (pathname) => {
  if (PUBLIC_EXACT.has(pathname)) return true;
  return PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
};

const NeutralSplash = () => (
  <div
    className="fixed inset-0 flex flex-col items-center justify-center bg-[#F8FAFC]"
    data-testid="neutral-splash"
  >
    <img src="/akountfly-icon.png" alt="" className="w-14 h-14 object-contain animate-pulse" />
    <div className="mt-5 w-24 h-1 rounded-full bg-slate-200 overflow-hidden">
      <div className="h-full w-1/3 bg-gradient-to-r from-[#283F8B] to-[#108981] animate-[loadbar_1.1s_ease-in-out_infinite]" />
    </div>
    <style>{`
      @keyframes loadbar { 0% { margin-left: -33%; } 100% { margin-left: 100%; } }
    `}</style>
  </div>
);

export default function SmartSuspenseFallback() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  // Sep 2026 · A route skeleton that stays up for 20s after an in-app
  // navigation means the lazy chunk request is hung (failures already reload
  // via the lazy wrapper). Hard-navigate once so the user is never left on a
  // permanent skeleton. Never on the initial load (POP) - slow first boots
  // must be allowed to finish.
  React.useEffect(() => {
    if (navType === 'POP') return undefined;
    const id = setTimeout(() => {
      const key = 'akf_suspense_hang_reload_at';
      const last = parseInt(sessionStorage.getItem(key) || '0', 10);
      if (Date.now() - last < 60000) return;
      sessionStorage.setItem(key, String(Date.now()));
      window.location.assign(window.location.href);
    }, 20000);
    return () => clearTimeout(id);
  }, [pathname, navType]);
  if (isPublic(pathname)) return <NeutralSplash />;
  return <AppShellSkeleton />;
}
